import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography, Card, Spin, Button, Table, Radio, Space, message, Tag, Tooltip, Empty, Select,
} from 'antd';
import {
  FullscreenOutlined, FullscreenExitOutlined, PlusOutlined,
  DeleteOutlined, FunctionOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Brush,
} from 'recharts';
import GlobalFilterBar from '../components/common/GlobalFilterBar';
import DownloadButton from '../components/common/DownloadButton';
import DataSourceSelector, {
  getAllSelectedFieldKeys, FIELD_LOOKUP,
} from '../components/common/DataSourceSelector';
import FormulaBuilder from '../components/common/FormulaBuilder';
import { useFilters } from '../context/FilterContext';
import { getExplorerUnified } from '../services/api';
import { applyFormulas } from '../utils/formulaEngine';
import dayjs from 'dayjs';
import { exportCardAsPng, exportTableAsCsv } from '../utils/downloadUtils';
import { tooltipSorter, formatNumberKM } from '../utils/formatters';

/* ─────────────── constants ─────────────── */

const CHART_COLORS = [
  '#0052CC', '#27AE60', '#E74C3C', '#8E44AD', '#F39C12',
  '#1ABC9C', '#34495E', '#D35400', '#2980B9', '#C0392B',
  '#16A085', '#7D3C98', '#D4AC0D', '#2E86C1', '#A93226',
];

const GREEN_SHADES = [
  '#C9F2C9', // very light green
  '#A3E4A3', // light green
  '#7CCD7C', // soft green
  '#54B254', // medium green
  '#2E9B2E', // kelly green
  '#1B821B', // forest green
  '#0E690E', // dark green
  '#065106', // very dark green
  '#023A02', // extremely dark green
  '#002400', // almost black green
];

const NON_GREEN_COLORS = [
  '#0052CC', // Blue
  '#E74C3C', // Red
  '#8E44AD', // Purple
  '#F39C12', // Orange
  '#34495E', // Slate/Dark Blue
  '#D35400', // Dark Orange
  '#2980B9', // Light Blue
  '#C0392B', // Dark Red
  '#7D3C98', // Dark Purple
  '#D4AC0D', // Yellow/Gold
  '#2E86C1', // Blue
  '#A93226', // Crimson Red
];

/* ─────────────── component ─────────────── */

export default function ExplorerDashboard() {
  const f = useFilters();

  // ── Core state ──
  const [selectedFields, setSelectedFields] = useState({});  // { revenue: ['gross_revenue', ...], ... }
  const [formulas, setFormulas] = useState([]);                // formula definitions
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── UI state ──
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);
  const [formulaModalVisible, setFormulaModalVisible] = useState(false);
  const [selectedYColumns, setSelectedYColumns] = useState([]); // which columns to actually plot

  /* ─────── derived ─────── */

  const allSelectedFieldKeys = useMemo(
    () => getAllSelectedFieldKeys(selectedFields),
    [selectedFields]
  );

  const formulaNames = useMemo(
    () => formulas.map(f => f.name),
    [formulas]
  );

  // All plottable keys = selected fields + formula columns
  const allAvailableKeys = useMemo(
    () => [...allSelectedFieldKeys, ...formulaNames],
    [allSelectedFieldKeys, formulaNames]
  );

  // Compute data with formulas applied
  const computedData = useMemo(() => {
    if (!rawData.length) return [];

    // Only keep selected fields + period in each row
    let data = rawData.map(row => {
      const out = { period: row.period };
      if ((f.granularity === '15min' || f.granularity === '15min-raw') && typeof row.period === 'string' && row.period.includes(':')) {
        const timePart = row.period.includes(' ') ? row.period.split(' ')[1] : row.period;
        const parts = timePart.split(':');
        out.block_number = Math.floor((parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)) / 15) + 1;
      }
      allSelectedFieldKeys.forEach(key => {
        const v = Number(row[key]);
        out[key] = isNaN(v) ? 0 : v;
      });
      return out;
    });

    // Apply formula columns
    if (formulas.length > 0) {
      data = applyFormulas(data, formulas);
    }

    return data;
  }, [rawData, allSelectedFieldKeys, formulas]);

  /* ─────── API fetch ─────── */

  const fetchData = useCallback(async () => {
    if (allSelectedFieldKeys.length === 0) return;
    setLoading(true);
    try {
      const res = await getExplorerUnified(f.apiParams());
      const results = res.data?.results || [];
      setRawData(results);
    } catch (err) {
      console.error(err);
      message.error('Failed to fetch explorer data');
    }
    setLoading(false);
  }, [f, allSelectedFieldKeys.length]);

  // Auto-select Y columns when fields change
  useEffect(() => {
    setSelectedYColumns(prev => {
      const valid = prev.filter(c => allAvailableKeys.includes(c));
      // If nothing valid left, auto-select first few
      if (valid.length === 0 && allAvailableKeys.length > 0) {
        return allAvailableKeys.slice(0, Math.min(3, allAvailableKeys.length));
      }
      // Add newly added fields/formulas
      const newKeys = allAvailableKeys.filter(k => !prev.includes(k));
      return [...valid, ...newKeys];
    });
  }, [allAvailableKeys]);

  /* ─────── formula handlers ─────── */

  const handleAddFormula = (formula) => {
    setFormulas(prev => [...prev, formula]);
    setFormulaModalVisible(false);
  };

  const removeFormula = (id, name) => {
    setFormulas(prev => prev.filter(f => f.id !== id));
    setSelectedYColumns(prev => prev.filter(c => c !== name));
  };

  /* ─────── table columns ─────── */

  const tableColumns = useMemo(() => {
    const cols = [
      {
        title: 'Date', dataIndex: 'period', key: 'period', fixed: 'left', width: 150, render: (val, record) => {
          if (f.granularity === 'hourly' || f.granularity === '15min' || f.granularity === '15min-raw') {
            return record.date ? `${record.date} ${val}` : val;
          }
          return val;
        }
      },
      ...((f.granularity === '15min' || f.granularity === '15min-raw') ? [{ title: 'Block Number', dataIndex: 'block_number', key: 'block_number', width: 120 }] : []),
    ];
    allAvailableKeys.forEach(key => {
      if (selectedYColumns.includes(key)) {
        const meta = FIELD_LOOKUP[key];
        cols.push({
          title: meta?.label || key,
          dataIndex: key,
          key,
          render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '—',
          sorter: (a, b) => (Number(a[key]) || 0) - (Number(b[key]) || 0),
          width: 140,
        });
      }
    });
    return cols;
  }, [allAvailableKeys, selectedYColumns]);

  /* ─────── chart axis formatting ─────── */

  const isHourlyOr15Min = f.granularity === 'hourly' || f.granularity === '15min' || f.granularity === '15min-raw';
  const axisInterval = f.granularity === '15min' ? 3 : (f.granularity === '15min-raw' ? 2 : 0);
  const axisTickProps = isHourlyOr15Min
    ? { fontSize: 10, angle: 0, textAnchor: 'middle', dy: 10 }
    : { fontSize: 11, angle: -45, textAnchor: 'end', dy: 15 };

  const formatTick = v => {
    if (!v) return '';
    if (isHourlyOr15Min) {
      if (typeof v === 'string' && v.includes(' ')) {
        const timeStr = v.split(' ')[1];
        if (f.granularity === 'hourly') return timeStr.substring(0, 2);
        return timeStr.substring(0, 5);
      }
      return v;
    }
    const d = dayjs(v);
    if (!d.isValid()) return v;
    if (f.granularity === 'monthly') return d.format('MMM-YY');
    if (f.granularity === 'yearly') return d.format('YYYY');
    return d.format('DD-MMM');
  };

  const brushProps = {};
  if ((isHourlyOr15Min || f.granularity === 'daily') && computedData.length > 0) {
    let maxVisiblePoints = 30;
    if (f.granularity === '15min' || f.granularity === '15min-raw') maxVisiblePoints = 96;
    if (f.granularity === 'hourly') maxVisiblePoints = 24;
    if (computedData.length > maxVisiblePoints) {
      brushProps.startIndex = computedData.length - maxVisiblePoints;
      brushProps.endIndex = computedData.length - 1;
    }
  }

  /* ─────── label helper ─────── */

  const getColumnLabel = (key) => {
    const meta = FIELD_LOOKUP[key];
    if (meta) return meta.label;
    const formula = formulas.find(f => f.name === key);
    if (formula) return key;
    return key;
  };

  /* ─────────────── render ─────────────── */

  const hasData = computedData.length > 0 && selectedYColumns.length > 0;

  return (
    <div id="dashboard-explorer" style={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      {/* ──── Main content area ──── */}
      <div style={{ flex: 1, padding: '24px', overflowX: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Data Explorer
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Cross-source analytics · Select fields, build formulas, visualize
            </Typography.Text>
          </div>
          <Space>
            {/* <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchData}
              disabled={allSelectedFieldKeys.length === 0}
            >
              Fetch Data
            </Button> */}
          </Space>
        </div>

        {/* ──── Data source selector + formula bar ──── */}
        <Card size="small" style={{ marginBottom: 16, background: '#f8fafc' }}>
          {/* Data source tree */}
          <DataSourceSelector
            selectedFields={selectedFields}
            onChange={setSelectedFields}
          />

          {/* Separator */}
          {allSelectedFieldKeys.length > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '12px 0', paddingTop: 12 }}>
              {/* Y-Axis column picker + formula button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Typography.Text strong style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  Plot Columns:
                </Typography.Text>
                <Select
                  mode="multiple"
                  placeholder="Select fields and formulas to plot"
                  style={{ flex: 1, minWidth: 280 }}
                  value={selectedYColumns}
                  onChange={setSelectedYColumns}
                  allowClear
                  maxTagCount="responsive"
                >
                  {allAvailableKeys.map(key => {
                    const meta = FIELD_LOOKUP[key];
                    const isFormula = formulas.some(f => f.name === key);
                    const label = meta?.label || key;
                    return (
                      <Select.Option key={key} value={key} label={label}>
                        {isFormula ? `🧮 ${label}` : label}
                      </Select.Option>
                    );
                  })}
                </Select>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={() => setFormulaModalVisible(true)}
                  disabled={allSelectedFieldKeys.length === 0}
                >
                  Add Formula
                </Button>
              </div>
            </div>
          )}

          {/* Active formulas strip */}
          {formulas.length > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 12, paddingTop: 10 }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>
                Formulas:
              </Typography.Text>
              <Space size={4} wrap>
                {formulas.map(formula => (
                  <Tooltip
                    key={formula.id}
                    title={formula.type === 'expression'
                      ? formula.expression
                      : `${formula.type}(${formula.baseCol}${formula.param ? ', ' + formula.param : ''})`
                    }
                  >
                    <span style={{
                      background: '#f3e8ff',
                      border: '1px solid #d8b4fe',
                      padding: '2px 8px',
                      borderRadius: 6,
                      fontSize: 11,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      color: '#7c3aed',
                      fontWeight: 500,
                    }}>
                      <FunctionOutlined style={{ fontSize: 10 }} />
                      {formula.name}
                      <DeleteOutlined
                        style={{ color: '#ef4444', cursor: 'pointer', fontSize: 10 }}
                        onClick={() => removeFormula(formula.id, formula.name)}
                      />
                    </span>
                  </Tooltip>
                ))}
              </Space>
            </div>
          )}
        </Card>

        {/* ──── Visualization ──── */}
        <Spin spinning={loading}>
          <Card
            id="explorer-chart-card"
            title={
              <Space>
                <span>Explorer Visualization</span>
                {/* {computedData.length > 0 && (
                  <Tag color="geekblue" style={{ margin: 0, fontSize: 11 }}>
                    {computedData.length} rows × {selectedYColumns.length} columns
                  </Tag>
                )} */}
              </Space>
            }
            size="small"
            style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : {}}
            extra={
              <Space>
                <Radio.Group
                  value={viewMode}
                  onChange={e => setViewMode(e.target.value)}
                  size="small"
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="chart">Chart</Radio.Button>
                  <Radio.Button value="table">Table</Radio.Button>
                </Radio.Group>
                {viewMode === 'chart' ? (
                  <DownloadButton
                    onPng={() => exportCardAsPng('explorer-chart-card', `Explorer_Chart_${dayjs().format('MMMYYYY')}`)}
                  />
                ) : (
                  <DownloadButton
                    onCsv={() => exportTableAsCsv(computedData, tableColumns, `Explorer_Table_${dayjs().format('MMMYYYY')}`)}
                  />
                )}
                <Button
                  icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                />
              </Space>
            }
          >
            {viewMode === 'chart' ? (
              <div style={{ height: expanded ? 'calc(100vh - 120px)' : 450 }}>
                {hasData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={computedData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="period"
                        interval={axisInterval}
                        height={70}
                        tick={axisTickProps}
                        tickFormatter={formatTick}
                      />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatNumberKM(v)} />
                      <RechartsTooltip
                        itemSorter={tooltipSorter}
                        shared={true}
                        labelFormatter={(label) => {
                          if (f.granularity === '15min-raw') {
                            const d = dayjs(label);
                            return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                          }
                          if (isHourlyOr15Min) {
                            if (typeof label === 'string' && label.includes(' ')) {
                              const [dPart, tPart] = label.split(' ');
                              const t = f.granularity === 'hourly'
                                ? tPart.substring(0, 2) + ':00'
                                : tPart.substring(0, 5);
                              return `Time: ${t} (${dayjs(dPart).format('DD MMM')})`;
                            }
                            return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                          }
                          const d = dayjs(label);
                          if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                          return d.format('DD MMM, YYYY');
                        }}
                        formatter={(v, n) => [
                          Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }),
                          n,
                        ]}
                      />
                      <Legend />
                      <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />

                      {(() => {
                        const gdamCols = selectedYColumns.filter(c => c.toLowerCase().includes('gdam'));
                        const nonGdamCols = selectedYColumns.filter(c => !c.toLowerCase().includes('gdam'));

                        return selectedYColumns.map((col) => {
                          const isGdam = col.toLowerCase().includes('gdam');
                          let strokeColor;
                          if (isGdam) {
                            const gdamIndex = gdamCols.indexOf(col);
                            strokeColor = GREEN_SHADES[gdamIndex % GREEN_SHADES.length];
                          } else {
                            const nonGdamIndex = nonGdamCols.indexOf(col);
                            strokeColor = NON_GREEN_COLORS[nonGdamIndex % NON_GREEN_COLORS.length];
                          }

                          return (
                            <Line
                              key={col}
                              type="monotone"
                              dataKey={col}
                              name={getColumnLabel(col)}
                              stroke={strokeColor}
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 6 }}
                              connectNulls
                            />
                          );
                        });
                      })()}
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Empty
                      description={
                        allSelectedFieldKeys.length === 0
                          ? 'Select fields from above data sources"'
                          : rawData.length === 0
                            ? 'Click "Fetch Data" to load the selected fields'
                            : 'No columns selected for plotting'
                      }
                    />
                  </div>
                )}
              </div>
            ) : (
              <div style={{ height: expanded ? 'calc(100vh - 120px)' : 450, overflow: 'auto' }}>
                {hasData ? (
                  <Table
                    dataSource={computedData}
                    columns={tableColumns}
                    size="small"
                    scroll={{ x: 'max-content', y: expanded ? 'calc(100vh - 180px)' : 380 }}
                    pagination={{ pageSize: 50 }}
                    rowKey="period"
                  />
                ) : (
                  <Empty description="No data to display" style={{ marginTop: 80 }} />
                )}
              </div>
            )}
          </Card>
        </Spin>

        {/* ──── Formula Modal ──── */}
        <FormulaBuilder
          visible={formulaModalVisible}
          onCancel={() => setFormulaModalVisible(false)}
          onSave={handleAddFormula}
          selectedFieldKeys={allSelectedFieldKeys}
          existingFormulaNames={[...allSelectedFieldKeys, ...formulaNames]}
        />
      </div>

      {/* ──── Right sidebar: filters ──── */}
      <GlobalFilterBar onApply={fetchData} />
    </div>
  );
}
